// @ts-nocheck
import Papa from 'papaparse';

import type { RawGameRow } from './types';

// Per-field alias list, in priority order. The first non-empty value found in
// each row wins. Lookups are case-insensitive and whitespace-insensitive so
// "Division Name", "division name", and "DivisionName" all match.
const FIELD_ALIASES: Record<keyof Omit<RawGameRow, 'rowIndex'>, string[]> = {
  Division: ['Division', 'Division Name'],
  'Home Team': ['Home Team', 'Team 1 Name', 'Team 1'],
  'Away Team': ['Away Team', 'Team 2 Name', 'Team 2'],
  Date: ['Date', 'Game Date'],
  Time: ['Time', 'Game Time'],
  Location: ['Location', 'Court Number', 'Court'],
  Duration: ['Duration', 'Game Duration'],
};

const normalize = (s: string) => (s || '').toString().toLowerCase().replace(/[\s_\-]/g, '');

// Build a single lookup table {normalizedAlias -> canonicalField} for fast row reads.
const ALIAS_INDEX: Record<string, keyof Omit<RawGameRow, 'rowIndex'>> = (() => {
  const idx: Record<string, keyof Omit<RawGameRow, 'rowIndex'>> = {};
  for (const [canonical, aliases] of Object.entries(FIELD_ALIASES)) {
    for (const a of aliases) {
      idx[normalize(a)] = canonical as keyof Omit<RawGameRow, 'rowIndex'>;
    }
  }
  return idx;
})();

const ALL_ALIAS_KEYS = new Set(Object.keys(ALIAS_INDEX));

// Always-ignore (don't surface as "unknown") — common legacy ID columns that
// we explicitly don't use because admins type names, not IDs.
const KNOWN_IGNORED = new Set(
  ['Team 1 ID', 'Team 2 ID', 'Division ID', 'Home Team ID', 'Away Team ID'].map(normalize)
);

export interface ParseResult {
  rows: RawGameRow[];
  unknownHeaders: string[];
  parseErrors: string[];
}

function pickValue(row: Record<string, any>, canonical: keyof Omit<RawGameRow, 'rowIndex'>): string {
  for (const alias of FIELD_ALIASES[canonical]) {
    // Try exact-name first (PapaParse keeps original header strings).
    if (row[alias] != null && row[alias].toString().trim() !== '') {
      return row[alias].toString().trim();
    }
  }
  // Fall back to a normalized lookup across all keys in the row.
  for (const key of Object.keys(row)) {
    if (ALIAS_INDEX[normalize(key)] === canonical) {
      const v = row[key];
      if (v != null && v.toString().trim() !== '') return v.toString().trim();
    }
  }
  return '';
}

export function parseGamesCsv(file: File): Promise<ParseResult> {
  return new Promise((resolve) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: 'greedy',
      complete: (results) => {
        const fields: string[] = (results.meta?.fields || []).filter(Boolean);
        const unknownHeaders = fields.filter((h) => {
          const n = normalize(h);
          if (!n) return false;
          if (ALL_ALIAS_KEYS.has(n)) return false;
          if (KNOWN_IGNORED.has(n)) return false;
          return true;
        });

        const rows: RawGameRow[] = (results.data || []).map((row: any, idx: number) => ({
          rowIndex: idx + 1,
          Division: pickValue(row, 'Division'),
          'Home Team': pickValue(row, 'Home Team'),
          'Away Team': pickValue(row, 'Away Team'),
          Date: pickValue(row, 'Date'),
          Time: pickValue(row, 'Time'),
          Location: pickValue(row, 'Location'),
          Duration: pickValue(row, 'Duration'),
        }));

        const parseErrors = (results.errors || []).map(
          (e: any) => `Row ${(e.row ?? 0) + 1}: ${e.message}`
        );

        resolve({ rows, unknownHeaders, parseErrors });
      },
    });
  });
}

export const TEMPLATE_HEADERS: (keyof Omit<RawGameRow, 'rowIndex'>)[] = [
  'Division',
  'Home Team',
  'Away Team',
  'Date',
  'Time',
  'Location',
  'Duration',
];

export function buildTemplateCsv(sampleDivision = 'U18 Boys', sampleLocation = 'Main Court'): string {
  const headers = TEMPLATE_HEADERS.join(',');
  const sample = [
    sampleDivision,
    'Team A',
    'Team B',
    '2026-06-15',
    '10:00 AM',
    sampleLocation,
    '60',
  ].join(',');
  return `${headers}\n${sample}\n`;
}
